import { useState } from "react";
import { ActionItemDTO, CarriedActionItemDTO } from "@retrospecter/shared/types";
import { apiDelete, apiPatch, apiPost } from "../../api/client";
import { useTranslation } from "../../i18n";

interface ActionItemsProps {
  publicId: string;
  items: ActionItemDTO[];
  /** Still-open items from the previous retrospective, for review. */
  carried: CarriedActionItemDTO[];
  /** Whether this viewer may add, tick off and remove items. */
  canEdit: boolean;
  onChanged: () => void;
}

/**
 * The commitments a retrospective produces.
 *
 * Kept beside the board rather than inside a column: an action item is the
 * outcome of the discussion, not another sticky note to be voted on.
 */
export function ActionItems({
  publicId,
  items,
  carried,
  canEdit,
  onChanged,
}: ActionItemsProps) {
  const { t, plural } = useTranslation();
  const [text, setText] = useState("");
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const add = async (itemText: string, itemOwner: string | null) => {
    const clean = itemText.trim();
    if (!clean) return;

    setBusy(true);
    setError("");
    try {
      await apiPost(`/api/retrospectives/${publicId}/action-items`, {
        text: clean,
        owner: itemOwner?.trim() || null,
      });
      onChanged();
    } catch {
      setError(t("actions.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    await add(text, owner);
    setText("");
    setOwner("");
  };

  const setDone = async (item: ActionItemDTO, done: boolean) => {
    setError("");
    try {
      await apiPatch(`/api/action-items/${item.id}`, { done });
      onChanged();
    } catch {
      setError(t("actions.saveError"));
    }
  };

  const remove = async (item: ActionItemDTO) => {
    setError("");
    try {
      await apiDelete(`/api/action-items/${item.id}`);
      onChanged();
    } catch {
      setError(t("actions.saveError"));
    }
  };

  return (
    <section
      aria-label={t("actions.regionLabel")}
      data-testid="action-items"
      className="card mt-8 p-6"
    >
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[0.7rem] font-bold uppercase tracking-[0.08em] text-ink-muted">
          {t("actions.heading")}
        </h3>
        <p className="text-xs text-ink-faint">{t("actions.intro")}</p>
      </div>

      {carried.length > 0 && (
        <div className="mb-5 rounded-xl border border-amber-500/25 bg-amber-500/[0.07] p-4">
          <p className="mb-1 text-xs font-bold uppercase tracking-[0.06em] text-amber-700 dark:text-amber-300">
            {t("actions.carriedHeading")} · {plural("actions.carriedCount", carried.length)}
          </p>
          <p className="mb-3 text-xs text-ink-faint">{t("actions.carriedIntro")}</p>

          <ul className="space-y-2">
            {carried.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-2 text-sm text-ink-soft"
              >
                <span className="min-w-0">
                  <span className="break-words">{item.text}</span>{" "}
                  <span className="text-xs text-ink-faint">
                    ({t("actions.carriedFrom", { name: item.fromRetrospectiveName })})
                  </span>
                </span>
                {canEdit && (
                  <button
                    onClick={() => add(item.text, item.owner)}
                    disabled={busy}
                    className="btn-ghost px-2 py-1 text-xs"
                  >
                    {t("actions.carryOver")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-ink-faint">{t("actions.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-start gap-3 rounded-xl border border-line bg-surface-sunken/50 p-3"
            >
              <input
                type="checkbox"
                checked={item.done}
                disabled={!canEdit}
                onChange={(event) => setDone(item, event.target.checked)}
                aria-label={item.done ? t("actions.markNotDone") : t("actions.markDone")}
                className="mt-0.5 h-4 w-4 flex-shrink-0 accent-nm-accent"
              />

              <div className="min-w-0 flex-1">
                <p
                  className={`break-words text-sm ${
                    item.done ? "text-ink-faint line-through" : "text-ink"
                  }`}
                >
                  {item.text}
                </p>
                <p className="mt-0.5 text-xs text-ink-faint">
                  {item.owner || t("actions.unassigned")}
                </p>
              </div>

              {canEdit && (
                <button
                  onClick={() => remove(item)}
                  aria-label={t("actions.delete")}
                  className="rounded-md px-1.5 py-1 text-xs text-ink-faint transition-colors hover:bg-red-500/10 hover:text-red-500"
                >
                  <span aria-hidden="true">✕</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-line pt-4">
          <label className="min-w-[12rem] flex-1 text-xs text-ink-muted">
            {t("actions.textLabel")}
            <input
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  submit();
                }
              }}
              placeholder={t("actions.textPlaceholder")}
              data-testid="action-item-text"
              className="input-field mt-1 py-1.5 text-sm"
            />
          </label>

          <label className="w-40 text-xs text-ink-muted">
            {t("actions.ownerLabel")}
            <input
              value={owner}
              onChange={(event) => setOwner(event.target.value)}
              placeholder={t("actions.ownerPlaceholder")}
              data-testid="action-item-owner"
              className="input-field mt-1 py-1.5 text-sm"
            />
          </label>

          <button
            onClick={submit}
            disabled={busy || !text.trim()}
            data-testid="add-action-item"
            className="btn-accent px-4 py-2 text-sm"
          >
            {busy ? t("actions.adding") : t("actions.add")}
          </button>
        </div>
      )}

      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
    </section>
  );
}
